'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: string;
}

interface FAQSectionProps {
  className?: string;
}

const FAQSection = ({ className = '' }: FAQSectionProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const categories = [
    { id: 'all', name: 'All Questions', icon: 'QuestionMarkCircleIcon' },
    { id: 'ordering', name: 'Ordering Process', icon: 'ShoppingCartIcon' },
    { id: 'shipping', name: 'Shipping & Delivery', icon: 'TruckIcon' },
    { id: 'quality', name: 'Quality & Warranty', icon: 'ShieldCheckIcon' },
    { id: 'payment', name: 'Payment & Security', icon: 'CreditCardIcon' }
  ];

  const faqs: FAQ[] = [
    {
      id: '1',
      question: 'How does factory-direct pricing work?',
      answer: 'We eliminate all middleman markups by connecting you directly with our manufacturing facility. You pay only the production cost plus shipping, saving 40-60% compared to local distributors. Our transparent pricing includes all manufacturing, quality testing, and export documentation.',
      category: 'ordering'
    },
    {
      id: '2',
      question: 'What is the typical production and delivery timeline?',
      answer: 'Standard production takes 7-15 days depending on order size and customization requirements. Shipping typically adds 5-10 days for air freight or 20-30 days for sea freight. We provide real-time tracking and updates throughout the entire process.',
      category: 'shipping'
    },
    {
      id: '3',
      question: 'What quality certifications do your screens have?',
      answer: 'All our LED screens are certified with ISO 9001:2015, CE, FCC, RoHS, and UL standards. Each unit undergoes 48-hour aging tests, color calibration, and waterproof testing (IP65 for outdoor models). We provide complete certification documentation with every order.',
      category: 'quality'
    },
    {
      id: '4',
      question: 'What payment methods do you accept?',
      answer: 'We accept bank wire transfers (T/T), letters of credit (L/C), and PayPal for smaller orders. Payment terms are typically 70% deposit and 30% before shipping. All transactions are secured and we provide complete invoicing and documentation for your accounting department.',
      category: 'payment'
    },
    {
      id: '5',
      question: 'Can I customize the screen specifications?',
      answer: 'Absolutely! We specialize in custom manufacturing. You can specify exact dimensions, pixel pitch, brightness levels, cabinet design, mounting systems, and even custom branding. Our engineering team will work with you to create the perfect solution for your specific application.',
      category: 'ordering'
    },
    {
      id: '6',
      question: 'What warranty coverage is included?',
      answer: 'All screens include a comprehensive up to 5-year warranty covering manufacturing defects and component failures. We maintain spare parts inventory globally and provide remote technical support. Extended warranty options are available for mission-critical installations.',
      category: 'quality'
    },
    {
      id: '7',
      question: 'How do you handle international shipping and customs?',
      answer: 'We offer both DDP (Delivered Duty Paid) and FOB (Free On Board) shipping options. Our logistics team handles all export documentation, customs clearance, and delivery coordination. We use professional packaging standards and include full insurance coverage.',
      category: 'shipping'
    },
    {
      id: '8',
      question: 'Do you provide installation support?',
      answer: 'Yes! We provide detailed installation manuals, video guides, and remote technical support. For large projects, we can arrange on-site installation supervision or training. Our support team is available 24/7 via email, phone, and video call.',
      category: 'ordering'
    },
    {
      id: '9',
      question: 'What if I need to return or exchange a product?',
      answer: 'We stand behind our quality with a 30-day satisfaction guarantee. If there are any manufacturing defects or issues, we will arrange replacement at no cost. For custom orders, we provide detailed specifications and samples for approval before production begins.',
      category: 'quality'
    },
    {
      id: '10',
      question: 'How do I get started with an order?',
      answer: 'Simply fill out our quote request form with your project requirements. Our sales team will respond within 12 hours with a detailed proposal including specifications, pricing, and timeline. We can also arrange a virtual factory tour and product demonstration.',
      category: 'ordering'
    }
  ];

  const filteredFAQs = selectedCategory === 'all' 
    ? faqs 
    : faqs.filter(faq => faq.category === selectedCategory);

  const toggleFAQ = (id: string) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <section className={`py-16 md:py-24 bg-background ${className}`} aria-labelledby="faq-heading">
      <div className="container mx-auto px-5 md:px-10">
        <header className="text-center mb-12">
          <h2 id="faq-heading" className="font-headline font-bold text-3xl md:text-5xl text-primary mb-4">
            Frequently Asked Questions
          </h2>
          <p className="font-body text-lg md:text-xl text-text-secondary max-w-3xl mx-auto">
            Get answers to common questions about international LED screen purchasing
          </p>
        </header>

        <nav className="flex flex-wrap justify-center gap-3 mb-12" aria-label="FAQ categories">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              aria-pressed={selectedCategory === category.id}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-body text-sm transition-all duration-250 ${
                selectedCategory === category.id
                  ? 'bg-primary text-primary-foreground shadow-md'
                  : 'bg-surface text-text-primary hover:bg-muted'
              }`}
            >
              <Icon name={category.icon as any} size={18} variant="outline" aria-hidden="true" />
              <span>{category.name}</span>
            </button>
          ))}
        </nav>

        <div className="max-w-4xl mx-auto space-y-4" role="list" aria-label="Frequently asked questions">
          {filteredFAQs.map((faq) => (
            <article
              key={faq.id}
              className="bg-card rounded-lg shadow-card overflow-hidden transition-all duration-250"
              role="listitem"
            >
              <h3>
                <button
                  onClick={() => toggleFAQ(faq.id)}
                  aria-expanded={openId === faq.id}
                  aria-controls={`faq-answer-${faq.id}`}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-surface transition-colors duration-250"
                >
                  <span className="font-headline font-semibold text-lg text-primary pr-4">
                    {faq.question}
                  </span>
                  <Icon
                    name={openId === faq.id ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                    size={24}
                    variant="outline"
                    className="text-text-secondary flex-shrink-0"
                    aria-hidden="true"
                  />
                </button>
              </h3>

              {openId === faq.id && (
                <div id={`faq-answer-${faq.id}`} className="px-6 pb-6 animate-fadeIn">
                  <p className="font-body text-base text-text-secondary leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              )}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;